export type AdCtaType = 'url' | 'call' | 'whatsapp' | 'message' | 'none';

/** Every ad surface on Belivah, grouped the way advertisers choose them. */
export const AD_PLACEMENT_OPTIONS: { value: string; label: string; hint: string }[] = [
  { value: 'feed', label: 'News feeds', hint: 'Home feed, Testimonies and Group feeds (phone and desktop)' },
  { value: 'sidebar', label: 'Sidebar', hint: 'Right-hand column on laptops and desktops' },
  { value: 'video_midroll', label: 'Between Pulse videos', hint: 'Short break between Pulse videos from monetized creators' },
];

export const AD_CTA_OPTIONS: { value: AdCtaType; label: string }[] = [
  { value: 'url', label: 'Open a website' },
  { value: 'whatsapp', label: 'Chat on WhatsApp' },
  { value: 'call', label: 'Call a phone number' },
  { value: 'message', label: 'Message me on Belivah' },
  { value: 'none', label: 'No button' },
];

export const AD_BUTTON_TEXTS: Record<AdCtaType, string[]> = {
  url: ['Learn More', 'Shop Now', 'Order Now', 'Book Now', 'Register', 'Sign Up', 'Get Started', 'Donate', 'Watch More', 'Checkout'],
  whatsapp: ['Chat on WhatsApp', 'Message Us', 'Order Now', 'Contact Us'],
  call: ['Call Now', 'Contact Us', 'Book Now'],
  message: ['Message Us', 'Send Message', 'Contact Us', 'Ask a Question'],
  none: [],
};

/** Keeps the chosen button text if it suits the new action, otherwise picks that action's default. */
export function buttonTextFor(type: AdCtaType, current: string): string {
  const options = AD_BUTTON_TEXTS[type];
  if (!options.length) return '';
  return options.includes(current) ? current : options[0];
}

export function normalizeAdUrl(raw: string): string | null {
  const v = raw.trim();
  if (!v) return null;
  try {
    const u = new URL(/^[a-z][a-z0-9+.-]*:/i.test(v) ? v : `https://${v}`);
    if (!['http:', 'https:'].includes(u.protocol) || !u.hostname.includes('.')) return null;
    return u.toString();
  } catch {
    return null;
  }
}

function phoneDigits(raw: string): string | null {
  const v = raw.trim();
  const digits = v.replace(/\D/g, '');
  if (!/^\+?[\d\s\-()]+$/.test(v) || digits.length < 7 || digits.length > 15) return null;
  return digits;
}

/** Returns a user-facing problem with the call to action, or null when it is usable. */
export function validateAdCta(type: AdCtaType, value: string): string | null {
  if (type === 'url' && !normalizeAdUrl(value)) return 'Please enter a valid website link, for example example.com.';
  if ((type === 'call' || type === 'whatsapp') && !phoneDigits(value)) {
    return 'Please enter a valid phone number with the country code, for example +2348012345678.';
  }
  return null;
}

type ActionableAd = { actionType?: string | null; actionValue?: string | null; targetURL?: string | null; pageId?: string | null; createdBy?: string | null };

/** Links that open in a new tab must be opened synchronously in the click handler or browsers block them. */
export function opensNewTab(ad: ActionableAd): boolean {
  return (ad.actionType === 'url' && !!(ad.actionValue || ad.targetURL)) || ad.actionType === 'whatsapp';
}

/** Performs the ad's button action. `tab` is a blank window opened during the click, if any. */
export function runAdAction(ad: ActionableAd, tab: Window | null, decorateUrl: (url: string) => string = (u) => u) {
  const type = ad.actionType || 'url';
  const value = String(ad.actionValue || '');
  const openTab = (url: string) => {
    if (tab) tab.location.href = url;
    else window.open(url, '_blank', 'noopener');
  };
  if (type === 'url') {
    const url = normalizeAdUrl(value || ad.targetURL || '');
    if (url) openTab(decorateUrl(url));
    else tab?.close();
  } else if (type === 'whatsapp') {
    const digits = phoneDigits(value);
    if (digits) openTab(`https://wa.me/${digits}`);
    else tab?.close();
  } else if (type === 'call') {
    const digits = phoneDigits(value);
    if (digits) window.location.href = `tel:${value.trim().startsWith('+') ? '+' : ''}${digits}`;
  } else if (type === 'message') {
    if (ad.createdBy) window.location.href = `/messages?user=${encodeURIComponent(ad.createdBy)}`;
    else if (ad.pageId) window.location.href = `/page/${encodeURIComponent(ad.pageId)}`;
  }
}

export type AdBillingModel = 'cpm' | 'cpc';

/** Mirrors the server's AD_PRICING (USD): $1 per 1,000 views, $0.08 per click. */
export const AD_PRICES = { perThousandViews: 1, perClick: 0.08 } as const;

export const AD_BILLING_OPTIONS: { value: AdBillingModel; label: string; hint: string }[] = [
  { value: 'cpm', label: `Pay for views � $${AD_PRICES.perThousandViews.toFixed(2)} per 1,000 views`, hint: 'Best for awareness. Clicks are free.' },
  { value: 'cpc', label: `Pay per click � $${AD_PRICES.perClick.toFixed(2)} per click`, hint: 'Best for website visits, calls and messages. Views are free.' },
];

/** Rough reach a budget buys under the chosen billing model. */
export function estimateAdResults(budgetUsd: number, model: AdBillingModel): string {
  const b = Math.max(0, Number(budgetUsd) || 0);
  if (model === 'cpc') return `about ${Math.floor(b / AD_PRICES.perClick).toLocaleString()} clicks`;
  return `about ${Math.floor((b / AD_PRICES.perThousandViews) * 1000).toLocaleString()} views`;
}
